import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReportLogo from '../components/ReportLogo';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getMonthlyReports, downloadMonthlyReport } from '../utils/api';
import { ROLE_SUPERADMIN, ROLE_FACILITY_MANAGER } from '../utils/roles';

const ReportsPage = () => {
        const { user } = useContext(AuthContext);
        const { customerId: urlCustomerId } = useParams();
        const { showToast } = useToast();
        const customerId = urlCustomerId || user?.customerId;

        const [reports, setReports] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [downloading, setDownloading] = useState(null);

        const allowed = user?.roleId === ROLE_SUPERADMIN || user?.roleId === ROLE_FACILITY_MANAGER;

        useEffect(() => {
                if (!allowed || !customerId) return;
                const fetchReports = async () => {
                        try {
                                const res = await getMonthlyReports(customerId);
                                if (!res.ok) throw new Error('Failed to load reports');
                                setReports(await res.json());
                        } catch (err) {
                                console.error(err);
                                setError('Failed to load reports');
                        } finally {
                                setLoading(false);
                        }
                };
                fetchReports();
        }, [allowed, customerId]);

        const handleDownload = async (period) => {
                setDownloading(period);
                try {
                        if (!(await downloadMonthlyReport(customerId, period))) {
                                showToast('error', 'Failed to download report');
                        }
                } catch (err) {
                        console.error(err);
                        showToast('error', 'Failed to download report');
                } finally {
                        setDownloading(null);
                }
        };

        if (!allowed) {
                return <Navigate to="/" replace />;
        }

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header />

                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <section className="mb-8">
                                        <h1 className="text-3xl font-bold text-text-strong mb-2">Reports</h1>
                                        <p className="text-text-weak">A monthly overview of your plants, made on the 1st of each month.</p>
                                </section>

                                <ReportLogo customerId={customerId} />

                                <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-card overflow-hidden" data-testid="reports-list">
                                        {reports.length === 0 ? (
                                                <div className="px-6 py-12 text-center text-text-weak" data-testid="reports-empty">
                                                        Your first monthly report arrives on the 1st of next month.
                                                </div>
                                        ) : (
                                                <ul className="divide-y divide-gray-200">
                                                        {reports.map((r) => (
                                                                <li key={r.period} className="flex items-center justify-between gap-4 px-6 py-4" data-testid="report-row" data-period={r.period}>
                                                                        <div className="flex items-center gap-4 min-w-0">
                                                                                <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                                                                                        <FontAwesomeIcon icon={faFilePdf} className="text-brand" />
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                        <div className="font-semibold text-text-strong">{r.period_label}</div>
                                                                                        <div className="text-sm text-text-weak">
                                                                                                Made {new Date(r.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                                                                                        </div>
                                                                                </div>
                                                                        </div>
                                                                        <button
                                                                                onClick={() => handleDownload(r.period)}
                                                                                disabled={downloading === r.period}
                                                                                data-testid="report-download"
                                                                                className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand/90 transition-colors disabled:opacity-60"
                                                                        >
                                                                                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                                                                {downloading === r.period ? 'Downloading...' : 'Download PDF'}
                                                                        </button>
                                                                </li>
                                                        ))}
                                                </ul>
                                        )}
                                </div>
                        </main>

                        <Footer />
                </div>
        );
};

export default ReportsPage;
